import React, { useState, useEffect, useCallback, useMemo } from "react";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";
const MONO = "'JetBrains Mono', monospace";
const MUTED = "rgba(10,10,15,0.58)";

// Amazon gives ISO strings (cursor, connect time, order time).
const fmtIso = (s) => (s ? new Date(s).toLocaleString() : "—");

// fulfillmentStatus → plain words
const STATUS_LABELS = { UNSHIPPED: "Not shipped", PARTIALLY_SHIPPED: "Part shipped", SHIPPED: "Shipped" };

const cardTitle = { fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: MUTED, marginBottom: 12 };
const help = { fontSize: 13, color: MUTED, lineHeight: 1.6 };
const code = { fontFamily: MONO, color: "#0A0A0F" };

// SKU Mapping filter chips. "none" = needs a match, "broken" = mapped to a SKU
// that is no longer in Products.
const MAP_FILTERS = [
  { key: "todo", label: "Needs a match" },
  { key: "suggested", label: "Has a suggestion" },
  { key: "mapped", label: "Mapped" },
  { key: "direct", label: "Same SKU" },
  { key: "all", label: "All" },
];
const MAP_SORTS = [
  { key: "name", label: "Name" },
  { key: "sku", label: "Amazon SKU" },
  { key: "orders", label: "Most orders" },
];
const MATCH_LABELS = {
  mapped: { text: "Mapped", color: "#0c3a2a", bg: "rgba(52,211,153,0.15)" },
  direct: { text: "Same SKU", color: "#0033FF", bg: "rgba(0,51,255,0.08)" },
  none: { text: "Needs a match", color: "#b45309", bg: "rgba(245,158,11,0.15)" },
  broken: { text: "Product missing", color: "#ef4444", bg: "rgba(239,68,68,0.1)" },
  parent: { text: "Parent listing", color: MUTED, bg: "rgba(10,10,15,0.06)" },
};

const EMPTY_CRED = { empId: "", password: "", clientId: "", clientSecret: "", refreshToken: "" };

export default function AmazonSellerPage({ store }) {
  const storeKey = store === "vibratovault" ? "vibratovault" : "designshop";
  const storeLabel = storeKey === "designshop" ? "Design Shop" : "Vibrato Vault";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("orders");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [settings, setSettings] = useState({
    connected: false, appConfigured: false, sellerName: "", marketplaceId: "",
    lastSynced: "", connectedAt: "", tokenExpiresAt: "", lastError: "",
  });
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");
  const [syncing, setSyncing] = useState(false);
  // CEO-only credential form
  const [cred, setCred] = useState(EMPTY_CRED);
  const [credSaving, setCredSaving] = useState(false);
  const [showCredForm, setShowCredForm] = useState(false);
  // SKU Mapping
  const [mapRows, setMapRows] = useState([]);
  const [mapReport, setMapReport] = useState({ status: "", error: "", count: 0, finishedAt: "" });
  const [mapLoading, setMapLoading] = useState(false);
  const [mapFilter, setMapFilter] = useState("todo");
  const [mapSort, setMapSort] = useState("name");
  const [mapDrafts, setMapDrafts] = useState({});
  const [mapSaving, setMapSaving] = useState("");
  const [products, setProducts] = useState([]);
  const currentUser = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("nycmfg-user")); } catch { return null; }
  }, []);

  const fetchSettings = useCallback(() => {
    fetch(`${API_URL}/api/amazon-sp/settings?store=${storeKey}`)
      .then((r) => r.json())
      .then((s) => setSettings((prev) => ({ ...prev, ...s })))
      .catch(() => {});
  }, [storeKey]);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/amazon-sp/orders?store=${storeKey}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else if (data.orders) setOrders(data.orders);
        setLoading(false);
      })
      .catch((err) => { setError(`Fetch failed: ${err.message}`); setLoading(false); });
  }, [storeKey]);

  const fetchSkuMap = useCallback((quiet) => {
    if (!quiet) setMapLoading(true);
    fetch(`${API_URL}/api/amazon-sp/sku-map?store=${storeKey}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) setError(d.error);
        else { setMapRows(d.rows || []); setMapReport(d.report || {}); }
        setMapLoading(false);
      })
      .catch((err) => { setError(`Fetch failed: ${err.message}`); setMapLoading(false); });
  }, [storeKey]);

  const fetchProducts = useCallback(() => {
    fetch(`${API_URL}/api/products?store=${storeKey}`)
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : (d.products || []);
        const seen = new Set();
        setProducts(list.filter((p) => p.sku && !seen.has(p.sku) && seen.add(p.sku))
          .sort((a, b) => a.sku.localeCompare(b.sku)));
      })
      .catch(() => {});
  }, [storeKey]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { if (tab === "skumap") { fetchSkuMap(); fetchProducts(); } }, [tab, fetchSkuMap, fetchProducts]);

  // While Amazon builds the listings report, check back every 15 s.
  useEffect(() => {
    if (tab !== "skumap" || mapReport.status !== "running") return undefined;
    const iv = setInterval(() => fetchSkuMap(true), 15000);
    return () => clearInterval(iv);
  }, [tab, mapReport.status, fetchSkuMap]);
  useEffect(() => { if (tab === "orders") fetchOrders(); }, [tab, fetchOrders]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // No page-side auto-sync: Amazon allows ~1 order search per 3 minutes, and
  // the server poller already uses it every 5 minutes. Just refresh the log.
  useEffect(() => {
    if (!settings.connected) return undefined;
    const iv = setInterval(() => { fetchOrders(); fetchSettings(); }, 120000);
    return () => clearInterval(iv);
  }, [settings.connected, fetchOrders, fetchSettings]);

  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${storeLabel} from Amazon? Inventory stops auto-syncing until you connect again.`)) return;
    await fetch(`${API_URL}/api/amazon-sp/disconnect?store=${storeKey}`, { method: "POST" }).catch(() => {});
    fetchSettings();
    setToast("Disconnected from Amazon");
  };

  const syncNow = async () => {
    setSyncing(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/amazon-sp/sync?store=${storeKey}`, { method: "POST" });
      const d = await r.json();
      if (d.error) setError(d.rateLimited ? "Amazon is limiting requests right now. Wait 3 minutes and try again. The server keeps syncing by itself." : d.error);
      else {
        const errs = (d.errors || []).length;
        const extra = [
          d.pending ? `${d.pending} waiting for payment` : "",
          d.cancelled ? `${d.cancelled} cancelled` : "",
          errs ? `${errs} error${errs > 1 ? "s" : ""}` : "",
        ].filter(Boolean).join(" • ");
        setToast(`Synced ${d.synced} • skipped ${d.skipped}${extra ? ` • ${extra}` : ""}`);
        fetchOrders();
        fetchSettings();
      }
    } catch (e) { setError(`Sync failed: ${e.message}`); }
    setSyncing(false);
  };

  const saveCredentials = async () => {
    setCredSaving(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/amazon-sp/credentials`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...cred, store: storeKey }),
      });
      const d = await r.json();
      if (d.error) setError(d.error);
      else {
        setToast(d.connected ? `Connected to Amazon${d.sellerName ? ` — ${d.sellerName}` : ""} ✓` : "Amazon app keys saved ✓");
        setShowCredForm(false);
        setCred(EMPTY_CRED);
        fetchSettings();
      }
    } catch (e) { setError(`Save failed: ${e.message}`); }
    setCredSaving(false);
  };

  const loadAllSkus = async () => {
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/amazon-sp/sku-map/refresh?store=${storeKey}`, { method: "POST" });
      const d = await r.json();
      if (d.error) setError(d.error);
      else setMapReport((m) => ({ ...m, status: "running", error: "" }));
    } catch (e) { setError(`Request failed: ${e.message}`); }
  };

  const saveMapping = async (amazonSku, productSku) => {
    setMapSaving(amazonSku);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/amazon-sp/sku-map`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ store: storeKey, amazonSku, productSku, updatedBy: currentUser?.name || "", empId: currentUser?.empId || "" }),
      });
      const d = await r.json();
      if (d.error) setError(d.error);
      else {
        setToast(productSku ? `${amazonSku} → ${productSku} saved ✓` : `Match removed from ${amazonSku}`);
        setMapDrafts((m) => { const n = { ...m }; delete n[amazonSku]; return n; });
        fetchSkuMap(true);
      }
    } catch (e) { setError(`Save failed: ${e.message}`); }
    setMapSaving("");
  };

  const acceptMany = async (rows, label) => {
    const items = rows.filter((r) => r.suggestion).map((r) => ({ amazonSku: r.amazonSku, productSku: r.suggestion.sku }));
    if (!items.length) return;
    setMapSaving(label);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/amazon-sp/sku-map/batch`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ store: storeKey, items, updatedBy: currentUser?.name || "", empId: currentUser?.empId || "" }),
      });
      const d = await r.json();
      if (d.error) setError(d.error);
      else { setToast(`${d.saved} match${d.saved === 1 ? "" : "es"} saved ✓`); fetchSkuMap(true); }
    } catch (e) { setError(`Save failed: ${e.message}`); }
    setMapSaving("");
  };

  const productNames = useMemo(() => Object.fromEntries(products.map((p) => [p.sku, p.name || ""])), [products]);
  const mapCounts = useMemo(() => ({
    todo: mapRows.filter((r) => r.match === "none" || r.match === "broken").length,
    suggested: mapRows.filter((r) => r.suggestion).length,
    mapped: mapRows.filter((r) => r.match === "mapped").length,
    direct: mapRows.filter((r) => r.match === "direct").length,
    all: mapRows.length,
  }), [mapRows]);
  const filteredMap = mapRows.filter((r) => {
    if (mapFilter === "todo" && !(r.match === "none" || r.match === "broken")) return false;
    if (mapFilter === "mapped" && r.match !== "mapped") return false;
    if (mapFilter === "suggested" && !r.suggestion) return false;
    if (mapFilter === "direct" && r.match !== "direct") return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return [r.amazonSku, r.title, r.options, r.asin, r.productSku, r.productName].some((v) => (v || "").toLowerCase().includes(q));
  }).sort((a, b) => {
    if (mapSort === "sku") return a.amazonSku.localeCompare(b.amazonSku);
    if (mapSort === "orders" && a.orders !== b.orders) return b.orders - a.orders;
    // By listing name, then option, so all options of one product sit together.
    // Rows with no name go last.
    if (!a.title !== !b.title) return a.title ? -1 : 1;
    return (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" })
      || (a.options || "").localeCompare(b.options || "", undefined, { numeric: true })
      || a.amazonSku.localeCompare(b.amazonSku);
  });

  const filteredOrders = orders.filter((o) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (o.title || "").toLowerCase().includes(s)
      || (o.sku || "").toLowerCase().includes(s)
      || (o.orderId || "").toLowerCase().includes(s)
      || (o.items || []).some((i) => (i.sku || "").toLowerCase().includes(s));
  });

  const tabs = [
    { key: "orders", label: "Orders" },
    { key: "skumap", label: "SKU Mapping" },
    { key: "connect", label: "Connection" },
  ];

  const formOpen = !settings.connected || !settings.appConfigured || showCredForm;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1 }}>Amazon Integration</h2>
          <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>
            direct Amazon Seller API · {storeLabel}{settings.sellerName ? ` · ${settings.sellerName}` : ""}
          </div>
        </div>
        {settings.connected && (
          <button onClick={syncNow} disabled={syncing} style={{ ...S.addBtn, opacity: syncing ? 0.6 : 1, cursor: syncing ? "wait" : "pointer" }}>
            {syncing ? "Syncing…" : "Sync Now"}
          </button>
        )}
      </div>

      <div style={{ ...S.nav, marginBottom: 20 }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => { setTab(t.key); setSearch(""); }} style={{ ...S.navBtn, ...(tab === t.key ? S.navBtnActive : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      {toast && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(52,211,153,0.12)", border: "1px solid #34d399", fontSize: 14, color: "#0c3a2a", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{toast}</span>
          <button onClick={() => setToast("")} style={{ background: "none", border: "none", color: "#0c3a2a", cursor: "pointer", fontSize: 16 }}>x</button>
        </div>
      )}

      {(error || settings.lastError) && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid #ef4444", fontSize: 14, color: "#ef4444", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span style={{ wordBreak: "break-word" }}>{error || `Last auto-sync failed: ${settings.lastError}`}</span>
          {error && <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16 }}>x</button>}
        </div>
      )}

      {/* Connection Tab */}
      {tab === "connect" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12 }}>
              <div style={{ ...cardTitle, marginBottom: 0 }}>Seller Account</div>
              {settings.connected && (
                <span style={{ fontSize: 13, color: "#34d399", display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, background: "#34d399", display: "inline-block" }} />
                  Connected{settings.sellerName ? ` — ${settings.sellerName}` : ""}
                </span>
              )}
            </div>
            {settings.connected && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", fontSize: 13, color: MUTED, marginBottom: 16 }}>
                  <span>Marketplace</span><span style={code}>{settings.marketplaceId} (Amazon.com)</span>
                  <span>Counting orders from</span><span style={code}>{fmtIso(settings.connectedAt)}</span>
                  <span>Last order change seen</span><span style={code}>{fmtIso(settings.lastSynced)}</span>
                </div>
                <p style={{ ...help, marginBottom: 16 }}>
                  The server checks Amazon for new orders every 5 minutes. It takes each sold SKU off {storeLabel} inventory one time, when Amazon confirms the payment. Cancelled orders and orders that Amazon ships from its own warehouse (FBA) do not change stock.
                </p>
              </>
            )}
            {formOpen ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 460 }}>
                <p style={{ ...help, margin: 0 }}>
                  Paste the three values from Seller Central (see the setup steps below). The server checks the refresh token with Amazon before it saves it, keeps all three, and never shows them again. CEO login is required. Leave a box empty to keep its saved value.
                </p>
                <input value={cred.clientId} onChange={(e) => setCred({ ...cred, clientId: e.target.value })} placeholder={settings.appConfigured ? "LWA Client ID (saved)" : "LWA Client ID"} style={S.input} />
                <input value={cred.clientSecret} onChange={(e) => setCred({ ...cred, clientSecret: e.target.value })} placeholder={settings.appConfigured ? "LWA Client secret (saved)" : "LWA Client secret"} type="password" style={S.input} />
                <input value={cred.refreshToken} onChange={(e) => setCred({ ...cred, refreshToken: e.target.value })} placeholder={settings.connected ? "Refresh token (saved)" : "Refresh token (starts with Atzr|)"} type="password" style={S.input} />
                <div style={{ display: "flex", gap: 10 }}>
                  <input value={cred.empId} onChange={(e) => setCred({ ...cred, empId: e.target.value })} placeholder="CEO Employee ID" style={{ ...S.input, flex: 1, minWidth: 0 }} />
                  <input value={cred.password} onChange={(e) => setCred({ ...cred, password: e.target.value })} placeholder="CEO Password" type="password" style={{ ...S.input, flex: 1, minWidth: 0 }} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={saveCredentials} disabled={credSaving} style={{ ...S.addBtn, opacity: credSaving ? 0.6 : 1 }}>{credSaving ? "Checking with Amazon…" : (settings.connected ? "Save" : `Connect ${storeLabel} to Amazon`)}</button>
                  {settings.connected && settings.appConfigured && <button onClick={() => setShowCredForm(false)} style={{ ...S.clearBtn, fontSize: 13 }}>Cancel</button>}
                </div>
                <div style={{ fontSize: 12, color: MUTED }}>Only orders placed after you connect come off inventory.</div>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={syncNow} disabled={syncing} style={{ ...S.addBtn, opacity: syncing ? 0.6 : 1 }}>{syncing ? "Syncing…" : "Sync Now"}</button>
                <button onClick={() => setShowCredForm(true)} style={{ ...S.clearBtn, fontSize: 13 }}>Update Keys</button>
                <button onClick={disconnect} style={{ ...S.clearBtn, fontSize: 13 }}>Disconnect</button>
              </div>
            )}
          </div>

          {/* One-time app setup */}
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={cardTitle}>Amazon App Setup (one time)</div>
            <ol style={{ ...help, paddingLeft: 18, margin: 0 }}>
              <li>In Seller Central, open Apps and Services → Develop Apps. Sign in with the account that sells {storeLabel} products. Your developer profile must be approved first.</li>
              <li>Select Add new app client. Choose <span style={code}>SP API</span> as the API type and turn on the <span style={code}>Inventory and Order Tracking</span> and <span style={code}>Product Listing</span> roles. Product Listing lets the SKU Mapping tab load all your Amazon SKUs. Do not ask for the buyer data (PII) roles. This app does not need them.</li>
              <li>In the app list, select View under LWA credentials. Copy the Client ID and the Client secret.</li>
              <li>Open the menu next to Edit App and select Authorize. Select Authorize app. Copy the refresh token. It starts with <span style={code}>Atzr|</span>.</li>
              <li>Paste the three values in Seller Account above and select Connect.</li>
            </ol>
          </div>
        </div>
      )}

      {/* SKU Mapping Tab */}
      {tab === "skumap" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={cardTitle}>SKU Mapping</div>
            <p style={{ ...help, margin: "0 0 12px" }}>
              Some old Amazon listings have random SKUs that Amazon does not let us change. Here you tell the system which {storeLabel} SKU each Amazon SKU really is. When that Amazon SKU sells, stock comes off the {storeLabel} SKU you pick. A match always wins over a same-name SKU, so you can change it at any time. Orders that synced before you added a match do not change.
            </p>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <button onClick={loadAllSkus} disabled={!settings.connected || mapReport.status === "running"} style={{ ...S.addBtn, opacity: (!settings.connected || mapReport.status === "running") ? 0.6 : 1 }}>
                {mapReport.status === "running" ? "Loading from Amazon…" : "Load all SKUs from Amazon"}
              </button>
              <div style={{ fontSize: 13, color: MUTED, flex: 1, minWidth: 200 }}>
                {mapReport.status === "running" && "Amazon makes a report of all your listings. This takes 1 to 10 minutes. You can leave this page."}
                {mapReport.status === "done" && `Last loaded ${fmtIso(mapReport.finishedAt)}: ${mapReport.count} Amazon SKUs.`}
                {mapReport.status === "stale" && "The last load stopped before it finished. Select the button again."}
                {!mapReport.status && "Without this, the list only shows SKUs from orders. This button adds every Amazon listing."}
              </div>
            </div>
            {mapReport.status === "error" && mapReport.error && (
              <div style={{ marginTop: 12, fontSize: 13, color: "#ef4444", wordBreak: "break-word" }}>Load failed: {mapReport.error}</div>
            )}
          </div>

          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={cardTitle}>Suggestions</div>
            <p style={{ ...help, margin: "0 0 12px" }}>
              The system suggests a {storeLabel} SKU from the Amazon name and option (for example "Olive Green · Single Rocker" → a SKU with SR and GRN). <b>Likely</b> means one product clearly fits. <b>Please check</b> means it is a best guess. A suggestion is not saved until you select Accept. Rows with no suggestion need a person to pick the SKU.
            </p>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {MAP_FILTERS.map((f) => (
              <button key={f.key} onClick={() => setMapFilter(f.key)} style={{ ...S.navBtn, ...(mapFilter === f.key ? S.navBtnActive : {}), fontSize: 13 }}>
                {f.label} ({mapCounts[f.key]})
              </button>
            ))}
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search SKU, title or option..." style={{ ...S.input, flex: 1, maxWidth: 300, minWidth: 160 }} />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: -8 }}>
            <span style={{ fontSize: 12, color: MUTED, textTransform: "uppercase", letterSpacing: 1 }}>Sort by</span>
            {MAP_SORTS.map((o) => (
              <button key={o.key} onClick={() => setMapSort(o.key)} style={{ ...S.navBtn, ...(mapSort === o.key ? S.navBtnActive : {}), fontSize: 12 }}>
                {o.label}
              </button>
            ))}
            <span style={{ fontSize: 12, color: MUTED }}>
              {mapSort === "name" ? "All options of one product stay together." : mapSort === "orders" ? "SKUs that sell the most come first. Match these first." : "A to Z by the SKU on Amazon."}
            </span>
          </div>

          <datalist id="amazon-ds-skus">
            {products.map((p) => <option key={p.sku} value={p.sku}>{p.name}</option>)}
          </datalist>

          {mapLoading ? (
            <div style={{ textAlign: "center", padding: 40, color: MUTED }}>Loading SKUs...</div>
          ) : filteredMap.length === 0 ? (
            <div style={{ ...S.lineCard, padding: 24, textAlign: "center", color: MUTED, fontSize: 14 }}>
              {mapRows.length === 0 ? "No Amazon SKUs yet. Select Load all SKUs from Amazon, or wait for orders to sync."
                : mapFilter === "todo" ? "Every Amazon SKU has a match." : "No matching SKUs"}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {filteredMap.map((r, idx) => {
                const groupStart = mapSort === "name" && (idx === 0 || (filteredMap[idx - 1].title || "") !== (r.title || ""));
                const group = groupStart ? filteredMap.filter((x) => (x.title || "") === (r.title || "")) : [];
                const groupSuggested = group.filter((x) => x.suggestion);
                const badge = MATCH_LABELS[r.match] || MATCH_LABELS.none;
                const draft = mapDrafts[r.amazonSku];
                const value = draft !== undefined ? draft : r.productSku;
                const changed = draft !== undefined && draft.trim() !== r.productSku;
                const draftName = productNames[(value || "").trim()];
                const saving = mapSaving === r.amazonSku;
                const sug = r.suggestion;
                return (
                  <React.Fragment key={r.amazonSku}>
                  {groupStart && group.length > 1 && (
                    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: idx === 0 ? 0 : 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0A0A0F", flex: 1, minWidth: 200, wordBreak: "break-word" }}>
                        {r.title || "No name"} <span style={{ fontWeight: 400, color: MUTED }}>· {group.length} SKUs</span>
                      </div>
                      {groupSuggested.length > 0 && (
                        <button onClick={() => acceptMany(groupSuggested, r.title)} disabled={!!mapSaving} style={{ ...S.addBtn, padding: "6px 12px", fontSize: 12, opacity: mapSaving ? 0.6 : 1 }}>
                          {mapSaving === r.title ? "Saving…" : `Accept all ${groupSuggested.length} suggestions`}
                        </button>
                      )}
                    </div>
                  )}
                  <div style={{ ...S.lineCard, padding: 14, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
                    {r.imageUrl ? (
                      <img src={r.imageUrl} alt="" loading="lazy" style={{ width: 64, height: 64, objectFit: "contain", background: "#FFFFFF", border: "1px solid rgba(10,10,15,0.12)", flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 64, height: 64, background: "rgba(10,10,15,0.05)", flexShrink: 0 }} />
                    )}
                    <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontFamily: MONO, fontSize: 13, color: "#0A0A0F", wordBreak: "break-all" }}>{r.amazonSku}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 6px", color: badge.color, background: badge.bg }}>{badge.text}</span>
                      </div>
                      <div style={{ fontSize: 13, color: "#0A0A0F", marginTop: 4, wordBreak: "break-word" }}>{r.title || "—"}</div>
                      {r.options && (
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#0A0A0F", marginTop: 4 }}>Option: {r.options}</div>
                      )}
                      {r.match === "parent" && (
                        <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>This is the group listing that holds the options. Amazon never sells it, so it does not need a match.</div>
                      )}
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
                        {[r.asin && <a key="a" href={`https://www.amazon.com/dp/${r.asin}`} target="_blank" rel="noreferrer" style={{ color: "#0033FF" }}>ASIN {r.asin}</a>, r.listingStatus, r.quantity != null && `${r.quantity} on Amazon`,
                          `${r.orders} order${r.orders === 1 ? "" : "s"}`, r.source === "order" && "seen on an order"].filter(Boolean)
                          .map((x, i) => <React.Fragment key={i}>{i > 0 && " · "}{x}</React.Fragment>)}
                      </div>
                    </div>
                    <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: MUTED, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{storeLabel} SKU</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <input list="amazon-ds-skus" value={value}
                          placeholder={r.match === "direct" ? `${r.amazonSku} (same SKU)` : "Type or pick a SKU"}
                          onChange={(e) => setMapDrafts((m) => ({ ...m, [r.amazonSku]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter" && changed) saveMapping(r.amazonSku, value.trim()); }}
                          style={{ ...S.input, flex: 1, minWidth: 0, fontFamily: MONO, fontSize: 13 }} />
                        {changed && (
                          <button onClick={() => saveMapping(r.amazonSku, value.trim())} disabled={saving} style={{ ...S.addBtn, padding: "6px 12px", opacity: saving ? 0.6 : 1 }}>
                            {saving ? "…" : "Save"}
                          </button>
                        )}
                        {!changed && r.productSku && (
                          <button onClick={() => saveMapping(r.amazonSku, "")} disabled={saving} style={{ ...S.clearBtn, fontSize: 12 }}>Remove</button>
                        )}
                      </div>
                      <div style={{ fontSize: 12, marginTop: 4, color: (changed ? draftName : r.productName) ? MUTED : "#ef4444", wordBreak: "break-word" }}>
                        {changed
                          ? (value.trim() ? (draftName || "This SKU is not in Products") : "Save to remove the match")
                          : r.match === "broken" ? `${r.productSku} is not in Products any more`
                          : r.productName || (r.match === "none" ? "No match. Sales of this SKU do not change stock." : "")}
                        {!changed && r.updatedBy && r.match === "mapped" ? ` · set by ${r.updatedBy}` : ""}
                      </div>
                      {sug && !changed && (
                        <div style={{ marginTop: 8, padding: "8px 10px", background: sug.confidence === "high" ? "rgba(52,211,153,0.12)" : "rgba(245,158,11,0.12)", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: "#0A0A0F", wordBreak: "break-word" }}>
                            <b>{sug.confidence === "high" ? "Likely" : "Please check"}:</b> <span style={{ fontFamily: MONO }}>{sug.sku}</span>
                            {sug.name ? ` — ${sug.name}` : ""}
                            <div style={{ color: MUTED, marginTop: 2 }}>{sug.note}</div>
                          </div>
                          <button onClick={() => saveMapping(r.amazonSku, sug.sku)} disabled={saving} style={{ ...S.addBtn, padding: "4px 10px", fontSize: 12, opacity: saving ? 0.6 : 1 }}>
                            {saving ? "…" : "Accept"}
                          </button>
                        </div>
                      )}
                      {!sug && r.hint === "missing_product" && (
                        <div style={{ marginTop: 8, fontSize: 12, color: MUTED }}>
                          This looks like a real SKU, but no {storeLabel} product has it. Add the product on the Products page with SKU <span style={code}>{r.amazonSku}</span>. Then it matches by itself.
                        </div>
                      )}
                    </div>
                  </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Orders Tab */}
      {tab === "orders" && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders..." style={{ ...S.input, flex: 1, maxWidth: 300, minWidth: 0 }} />
            <div style={{ fontSize: 14, color: MUTED }}>{filteredOrders.length} orders</div>
            <button onClick={fetchOrders} disabled={loading} style={{ ...S.iconBtn, opacity: loading ? 0.5 : 1 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
              </svg>
            </button>
          </div>

          {!settings.connected && orders.length === 0 && (
            <div style={{ ...S.lineCard, padding: 24, textAlign: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 16, color: MUTED, marginBottom: 8 }}>Not connected to Amazon yet</div>
              <button onClick={() => setTab("connect")} style={S.addBtn}>Connect to Amazon</button>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: MUTED }}>Loading orders...</div>
          ) : (
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Order #</th>
                    <th style={S.th}>Item</th>
                    <th style={S.th}>SKU</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Amount</th>
                    <th style={{ ...S.th, textAlign: "center" }}>Qty Deducted</th>
                    <th style={S.th}>Ordered</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o) => {
                    const items = o.items || [];
                    const hasMultiple = items.length > 1;
                    const isExpanded = expandedOrder === o.id;
                    return (
                      <React.Fragment key={o.id}>
                        <tr onClick={() => hasMultiple && setExpandedOrder(isExpanded ? null : o.id)} style={{ cursor: hasMultiple ? "pointer" : "default" }}>
                          <td style={{ ...S.td, fontFamily: MONO, fontSize: 12 }}>
                            {hasMultiple && (
                              <span style={{ display: "inline-block", width: 16, color: MUTED, fontSize: 12 }}>{isExpanded ? "▾" : "▸"}</span>
                            )}
                            {o.orderId}
                            {o.status && <div style={{ fontSize: 11, color: MUTED }}>{STATUS_LABELS[o.status] || o.status}</div>}
                          </td>
                          <td style={{ ...S.td, maxWidth: 220 }}>
                            <div style={{ fontSize: 13, color: "#0A0A0F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.title || "—"}</div>
                          </td>
                          <td style={{ ...S.td, fontFamily: MONO, fontSize: 12, color: o.sku ? "#0A0A0F" : MUTED }}>{o.sku || "—"}</td>
                          <td style={{ ...S.td, textAlign: "right", fontFamily: MONO, color: "#0033FF" }}>
                            {o.amount ? `$${Number(o.amount).toFixed(2)}` : "—"}
                          </td>
                          <td style={{ ...S.td, textAlign: "center", fontFamily: MONO, color: o.quantityDeducted > 0 ? "#34d399" : MUTED }}>
                            {o.quantityDeducted > 0 ? `-${o.quantityDeducted}` : "0"}
                          </td>
                          <td style={{ ...S.td, fontSize: 13, color: MUTED }}>{fmtIso(o.orderCreatedAt || o.syncedAt)}</td>
                        </tr>
                        {isExpanded && items.map((item, i) => (
                          <tr key={`${o.id}-${i}`} style={{ background: "#FFFFFF" }}>
                            <td style={{ ...S.td, paddingLeft: 32 }} />
                            <td style={{ ...S.td, maxWidth: 220 }}>
                              <div style={{ fontSize: 12, color: MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.title || "—"}</div>
                            </td>
                            <td style={{ ...S.td, fontFamily: MONO, fontSize: 11, color: MUTED }}>{item.sku || "—"}{item.mappedSku ? ` → ${item.mappedSku}` : ""}</td>
                            <td style={S.td} />
                            <td style={{ ...S.td, textAlign: "center", fontFamily: MONO, fontSize: 12, color: item.deducted > 0 ? "#34d399" : MUTED }}>
                              {item.quantity || 0} qty{item.deducted > 0 ? ` (−${item.deducted})` : ""}
                            </td>
                            <td style={S.td} />
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {filteredOrders.length === 0 && !loading && (
                    <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: MUTED, padding: 24 }}>
                      {orders.length === 0 ? "No orders yet. Connect to Amazon and new orders sync here by themselves." : "No matching orders"}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
